import * as React from 'react';
import { styled } from '@mui/system';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

export function useInterval(callback: Function, delay: number | null) {
  const savedCallback = React.useRef<Function>(() => {});

  React.useEffect(() => {
      savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
      function tick() {
          savedCallback.current();
      }

      if (delay !== null) {
          let id = setInterval(tick, delay);
          return () => clearInterval(id);
      }

      return () => { };
  }, [delay]);
}

type InputNumberProps = { steps: number, onChange?: (value: number) => void, myvalue:number|null };

interface minMax {
    min: number;
    max: number;
    value: number | undefined;
    getValue: (value:number) => any
}

export default function InputNumber({ steps, onChange, myvalue }: InputNumberProps = { steps: 1, myvalue:null })
{
  const [value, setValue] = React.useState<number>(0);
    const [mouseDownDirection, setMouseDownDirection] = React.useState<'up' | 'down' | undefined>(undefined);
    // Max digit length allowed in the field (negatives get one extra for the sign).
    const max = (num: number) => (num < 0 ? 8 : 7);

    useInterval(() => handleButtonChange(mouseDownDirection), mouseDownDirection ? 100 : null);

    const handleChange = ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        setValue(curr => {
            if (!Boolean(value)) { return 0; }
            const numeric = parseInt(value, 10);
            const maxLength = max(numeric);

            if (value.length > maxLength) {
                return curr;
            }

            return (value.length <= maxLength ? numeric : curr);
        });
    };

    const handleButtonChange = (direction?: 'up' | 'down') => {
        setValue(curr => {
            let next: number;

            switch (direction) {
                case "up":
                    next = curr + (steps || 1);
                    break;
                case "down":
                    next = curr - (steps || 1);
                    break;
                default:
                    next = curr;
                    break;
            }

            return `${next}`.length <= max(curr) ? next : curr;
        });
    };

    React.useEffect(() => {
      onChange && onChange(value);
    }, [value]);

    React.useEffect(() => {
      
      if(myvalue !== null) {
        setValue(myvalue);
      } 
        
    },[myvalue]);

    
            {/* <NumberInput aria-label="Quantity Input" 
            min={1} 
            max={100} 
            value={2} 
            onInputChange={(e)=> props.getValue(parseInt(Object(e.target).value))}
            onChange={(e)=> props.getValue(parseInt(Object(e.target).value))} 
             
          />; */}

  return <StyledInputRoot>
            <StyledButton 
            onClick={() => handleButtonChange("down")}
            onMouseDown={() => setMouseDownDirection(undefined)}
            onMouseOut={() => setMouseDownDirection(undefined)}
            onMouseUp={() => setMouseDownDirection(undefined)}
            ><RemoveIcon fontSize="small" /></StyledButton>
            <StyledInput
              step={1}
              value={value}
              min={0}
              max={100}
              minLength={100}
              // Grow the field once the value exceeds 3 digits so long qtys fit.
              style={String(value).length > 3 ? { width: `${3.1 + (String(value).length - 3) * 0.6}rem` } : undefined}
              onChange={handleChange}
              // onKeyUpCapture={() => handleButtonChange("up")}
              // onKeyDownCapture={() => handleButtonChange("down")}
            />
            {/* <input type="number"  step={steps} value={value} min={1} max={100} onChange={handleChange} /> */}
            <StyledButton 
            onClick={() => handleButtonChange("up")}
            onMouseDown={() => setMouseDownDirection(undefined)}
            onMouseUp={() => setMouseDownDirection(undefined)}
            onMouseOut={() => setMouseDownDirection(undefined)}
            ><AddIcon fontSize="small" /></StyledButton>
        </StyledInputRoot>
}

// export default function QuantityInput(props:minMax) {
//   return <InputNumber steps={2}  onChange={(e) => console.log(String(e))} />
// }

const blue = {
  100: '#daecff',
  200: '#b6daff',
  300: '#66b2ff',
  400: '#3399ff',
  500: '#007fff',
  600: '#0072e5',
  700: '#0059B2',
  800: '#004c99',
  900: '#2586c2'
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const StyledInputRoot = styled('div')(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 400;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[500]};
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  height:25px;
`,
);

const StyledInput = styled('input')(
  ({ theme }) => `
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 400;
  height:25px;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
  box-shadow: 0px 2px 4px ${
    theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'
  };
  border-radius: 8px;
  margin: 0 8px;
  padding: 10px 12px;
  outline: 0;
  min-width: 0;
  width: 3.1rem;
  text-align: center;

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? blue[700] : blue[200]};
  }

  &:focus-visible {
    outline: 0;
  }
`,
);

const StyledButton = styled('button')(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  border: 2px solid;
  border-radius: 999px;
  border-color: ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
  color: ${theme.palette.mode === 'dark' ? grey[200] : grey[900]};
  width: 25px;
  height: 25px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    cursor: pointer;
    background: ${theme.palette.mode === 'dark' ? blue[700] : blue[900]};
    border-color: ${theme.palette.mode === 'dark' ? blue[500] : '#196b9e'};
    color: ${grey[50]};
  }

  &:focus-visible {
    outline: 0;
  }

  &.increment {
    order: 1;
  }

`,
);
